import { RefObject, memo, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { DESCRIPTION_LINE_COUNT_LIMIT } from '@components/action-card';
import { SelectOption } from '@components/select';
import { getScopedTranslate, styledTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Font } from '@styles/theme';
import { abbreviateDate } from '@utils/date';

import { ConditionComparator, JourneyAction, JourneyActionValuesTypes } from 'api/types';
import { ACTION_TYPES, getActionLabel } from 'ui/actions/action-config';
import { NodeActions } from 'ui/journey-builder/node-types/common';
import {
  JourneyNodeActionCard,
  MoreItems,
  NodeContainer,
} from 'ui/journey-builder/node-types/node-container';
import { JourneyNode } from 'ui/journey-builder/node-types/types';

const ACTION_TYPE = ACTION_TYPES.condition;

type GetConditionDescriptionProps<Styled extends boolean = true> = {
  values: JourneyActionValuesTypes[typeof ACTION_TYPE];
  styled?: Styled;
  locale?: typeof descriptionLocale;
  inNode?: boolean;
  isTextClamped?: boolean;
  textClampedRef?: RefObject<HTMLDivElement>;
};

type ConditionDescriptionElement = JSX.Element & {
  ref?: RefObject<any>;
};

export function getComparitorLabel(key: ConditionComparator) {
  const { t } = getScopedTranslate(descriptionLocale);
  const COMPARITOR_MAPPER: Partial<{ [key in ConditionComparator]: string }> = {
    eq: t('is'),
    neq: t('isNot'),
    boolean_true: t('is'),
    boolean_false: t('is'),
    date_lt: t('before'),
    date_lte: t('isOrBefore'),
    date_gt: t('after'),
    date_gte: t('isOrAfter'),
    date_eq: t('is'),
    date_neq: t('isNot'),
  };
  return COMPARITOR_MAPPER[key as keyof typeof COMPARITOR_MAPPER];
}

export function getValueLabel(
  value: string | SelectOption | undefined,
  comparator?: string
): string {
  const { t } = getScopedTranslate(descriptionLocale);
  if (comparator === 'boolean_true') {
    return t('booleanTrue');
  } else if (comparator === 'boolean_false') {
    return t('booleanFalse');
  } else if (!value) {
    return '';
  } else if (typeof value === 'object') {
    return (value.label ?? value.value) as string;
  } else if (comparator?.startsWith('date')) {
    return abbreviateDate(value, 'en');
  }
  return value;
}

export function getConditionDescription(props: GetConditionDescriptionProps<false>): string;
export function getConditionDescription(
  props: GetConditionDescriptionProps<true>
): ConditionDescriptionElement;
export function getConditionDescription({
  values,
  styled = true,
  locale = descriptionLocale,
  inNode = true,
  textClampedRef,
}: GetConditionDescriptionProps<boolean>): ConditionDescriptionElement | string {
  const { t } = getScopedTranslate(locale);

  const conditions = values?.conditions || [];
  if (conditions.length === 0) {
    return t('configureAction');
  }

  const renderConditionsCount =
    conditions.length > DESCRIPTION_LINE_COUNT_LIMIT
      ? DESCRIPTION_LINE_COUNT_LIMIT - 1
      : DESCRIPTION_LINE_COUNT_LIMIT;

  const comparatorElements = conditions.map((condition, index) => {
    if (styled && inNode && index >= renderConditionsCount) {
      return null;
    }
    const { field, comparator, value } = condition;
    const valueLabel = getValueLabel(value as string | SelectOption, comparator);
    const fieldLabel = field?.variables && Object.values(field.variables)[0].label;

    if (!fieldLabel || !comparator || !valueLabel) {
      return '';
    }

    const comparatorString = getComparitorLabel(comparator) || comparator;

    if (styled) {
      const conjunction = values?.conditionType === 'all' ? t('core.and') : t('core.or');
      const commonProps = { font: 'regular14' as Font, style: { display: 'inline' } };
      return (
        <ConditionContainer key={index}>
          {styledTranslate({
            key: 'conditionDescription',
            variables: {
              conjunction: {
                text: index > 0 ? `${conjunction} ` : '',
                color: 'textSecondary',
                capitalize: true,
                ...commonProps,
              },
              field: {
                text: fieldLabel,
                color: 'elementBlue100',
                ...commonProps,
              },
              comparator: {
                text: comparatorString,
                color: 'textSecondary',
                ...commonProps,
              },
              value: {
                text: valueLabel,
                color: 'textSecondary',
                ...commonProps,
              },
            },
            translations: descriptionLocale,
          })}
        </ConditionContainer>
      );
    } else {
      return t('conditionDescriptionNoStyle', {
        field: fieldLabel,
        comparator: comparatorString,
        value: valueLabel,
      });
    }
  });
  if (styled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }} ref={textClampedRef}>
        {inNode && t('continueIf')}
        {comparatorElements}
      </div>
    );
  } else {
    const conditionStrings = comparatorElements as string[];
    const joinedConditionString = conditionStrings.join(
      values?.conditionType === 'all' ? `\n${t('and')} ` : `\n${t('or')} `
    );

    return t('continueIfNoStyle', { conditions: joinedConditionString });
  }
}

type GetConditionTooltipProps = Omit<GetConditionDescriptionProps, 'styled' | 'inNode'>;

export const getConditionTooltip = (props: GetConditionTooltipProps) => {
  const { values, isTextClamped } = props;
  const conditions = values?.conditions ?? [];
  const isAllConditionsVisible =
    conditions.length <= DESCRIPTION_LINE_COUNT_LIMIT && !isTextClamped;
  return isAllConditionsVisible ? '' : getConditionDescription({ values, styled: false });
};

export const getDescription = (action: JourneyAction<typeof ACTION_TYPE>): string => {
  return getConditionDescription({ values: action.values, styled: false });
};

const descriptionLocale = {
  en: {
    configureAction: 'Configure action',
    continueIf: 'Continue if\n',
    continueIfNoStyle: 'Continue if\n{conditions}',
    conditionDescription: '{conjunction}{field} {comparator} {value}',
    conditionDescriptionNoStyle: '{field} {comparator} {value}',
    is: 'is',
    isNot: 'is not',
    booleanTrue: 'true',
    booleanFalse: 'false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    and: 'AND',
    or: 'OR',
  },
};

export const Node = memo((node: JourneyNode<typeof ACTION_TYPE>) => {
  const values = node.data?.values;
  const textClampedRef = useRef<HTMLDivElement | null>(null);
  const [isTextClamped, setIsTextClamped] = useState(false);
  const descriptionText = getConditionDescription({ values, textClampedRef, styled: true });

  useEffect(() => {
    const descriptionChildren = [...(descriptionText?.ref?.current?.children ?? [])];
    const isDescriptionTextClamped = descriptionChildren.some((child) => {
      return child.scrollHeight > child.clientHeight;
    });

    setIsTextClamped(isDescriptionTextClamped);
  }, [descriptionText]);

  return (
    <NodeContainer node={node}>
      <JourneyNodeActionCard
        node={node}
        label={getActionLabel(ACTION_TYPE)}
        descriptionText={descriptionText}
        tooltip={getConditionTooltip({ values, isTextClamped })}
        renderBelowDescription={<MoreItems items={values?.conditions} />}
      >
        <NodeActions node={node} />
      </JourneyNodeActionCard>
    </NodeContainer>
  );
});

const ConditionContainer = styled.div({
  ...lineClamp('-webkit-inline-box', 1, true),
});
